/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useAppDispatch, useAppSelector} from '../../store/Store'
import {SearchList} from '../../../common/component/SearchList'
import { IFilterDto } from '../../../common/model/Core.model'
import {TagInputSearchControls} from '../../../common/component/TagInputSearchControls'
import { useContactGetByIdMutation, useContactLoadMoreMutation, useContactSearchMutation } from '../Contact.api'
import {
  selectContact,
  selectContactFiltersSelection,
  selectContactLoading,
  selectContacts,
  selectContactsPagination,
  setContactFiltersSelection,
} from '../Contact.slice'
import { ContactPreview } from '../components/ContactPreview'
import { IContactFilter } from '../Contact.model'
import { ContactDashboardListItem } from '../components/ContactDashboardListItem'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { getEntry, removeEntry, setEntry } from '../../../service/LocalStorage.service'

const CONTACT_FILTERS_LS_KEY = 'contact_filtersSelection'

export const ContactDashboardPage: FC = () => {
  const intl = useIntl()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.DIRECTORY'}),
      path: '/contacts',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchInitialData = useRef(false)
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)

  const filterKeys = {
    defaultKey: IContactFilter.DETAIL,
    defaultKeyList: [IContactFilter.ID, IContactFilter.DETAIL],
  }
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
  const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectContactFiltersSelection)

  const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
    if (!Array.isArray(candidateFilters)) {
      return []
    }
    return candidateFilters.filter((item): item is IFilterDto => {
      if (!item || typeof item !== 'object') {
        return false
      }
      const filter = item as IFilterDto
      return (
        typeof filter.id === 'string' &&
        filterKeys.defaultKeyList.includes(filter.id as IContactFilter) &&
        typeof filter.value === 'string' &&
        typeof filter.editable === 'boolean'
      )
    })
  }

  const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
    dispatch(setContactFiltersSelection(nextFilters))
    if (nextFilters.length > 0) {
      setEntry(CONTACT_FILTERS_LS_KEY, JSON.stringify(nextFilters))
    } else {
      removeEntry(CONTACT_FILTERS_LS_KEY)
    }
  }

  const setFilters = (nextFilters: IFilterDto[]) => {
    persistFiltersSelection(nextFilters)
  }

  const [search] = useContactSearchMutation()
  const [loadMore] = useContactLoadMoreMutation()
  const [getById] = useContactGetByIdMutation()

  const contacts = useAppSelector(selectContacts)
  const contactsPagination = useAppSelector(selectContactsPagination)
  const contact = useAppSelector(selectContact)
  const contactLoading = useAppSelector(selectContactLoading)

  useEffect(() => {
    let initialFilters: IFilterDto[] | undefined
    const savedFilters = getEntry(CONTACT_FILTERS_LS_KEY)
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
        persistFiltersSelection(sanitizedFilters)
        initialFilters = sanitizedFilters
      } catch {
        persistFiltersSelection([])
      }
    }

    if (initialFilters && initialFilters.length > 0) {
      const filterIds = initialFilters.map((filter) => filter.id)
      setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
    } else {
      setFilterSuggestions(filterKeys.defaultKeyList)
    }

    fetchInitialData(initialFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitialData = async (filtersOverride?: IFilterDto[]) => {
    const requestFilters = filtersOverride ?? filters
    try {
      setIsSearchLoading(true)
      if (!didFetchInitialData.current) {
        const request: any = {}
        if (requestFilters.length > 0) {
          request.filters = requestFilters
        }
        const response: any = await search(request)
        if (response.data) {
          setIsSearchControlEnabled(true)
          if (response.data.filters) {
            const filterIds = response.data.filters.map((filter) => filter.id)
            persistFiltersSelection(response.data.filters)
            setFilterSuggestions(
              filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key))
            )
          } else {
            const filterIds = requestFilters.map((filter) => filter.id)
            setFilterSuggestions(filterKeys.defaultKeyList.filter((key) => !filterIds.includes(key)))
          }
        }
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current) {
        await loadMore({
          pagination: {
            limit: contactsPagination?.limit,
            sort: contactsPagination?.sort,
            page: contactsPagination?.page,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const searchOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await search({
          pagination: {
            sort: contactsPagination?.sort,
          },
          filters: filters,
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchOnClickHandler = async () => {
    persistFiltersSelection([])
    setFilterSuggestions(filterKeys.defaultKeyList)
    await fetchInitialData([])
  }

  const rowOnClickHandler = async (id: string) => {
    setRowSelected(id)
    if (!contact || contact.id !== id) {
      await getById({id, fields: 'LIMITED'})
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='flex mt-10'>
        <div className='row g-5'>
          <SearchList
            loading={isSearchLoading}
            rowSelected={rowSelected}
            nextPage={contactsPagination?.page}
            nextPageOnClickHandler={nextPageOnClickHandler}
            searchControlsComponent={
              <TagInputSearchControls
                loading={isSearchLoading}
                enabled={isSearchControlEnabled}
                filterKeys={filterKeys}
                filterSuggestions={filterSuggestions}
                filters={filters}
                setFilterSuggestions={setFilterSuggestions}
                setFilters={setFilters}
                createOnClickPath='/contacts/new'
                createOnClickOpenNew={false}
                searchOnClickHandler={searchOnClickHandler}
                resetSearchResetOnClickHandler={resetSearchOnClickHandler}
              />
            }
            listComponent={contacts.map((contact, index) => (
              <div key={index}>
                <ContactDashboardListItem
                  data={contact}
                  rowSelected={rowSelected}
                  onRowClick={rowOnClickHandler}
                />
                {index !== 0 && <div className='separator'></div>}
              </div>
            ))}
          />

          {rowSelected && (
            <ContactPreview
              data={contact}
              loading={contactLoading}
              setRowSelected={setRowSelected}
            />
          )}
        </div>
      </div>
    </>
  )
}
